import MarketingLayout from "@/components/MarketingLayout";
import { ExternalLink } from "lucide-react";

const ISAKSHAM_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,fit=cover/YbNar5pQPVHKQvVK/teal-and-orange-paper-wellness-influencer-instagram-highlight-cover-set-3-xKSSPkrh1zGQI3qw.png";
const KIPP_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,fit=cover/YbNar5pQPVHKQvVK/teal-and-orange-paper-wellness-influencer-instagram-highlight-cover-set-2-1IqvIqw4gJg9c9pn.png";
const SCW_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,fit=cover/YbNar5pQPVHKQvVK/teal-and-orange-paper-wellness-influencer-instagram-highlight-cover-set-1-FSQmOamOHgeucUfI.png";

const projects = [
  {
    title: "KIPP Texas Public Schools",
    location: "Texas, USA",
    status: "Active",
    image: KIPP_IMG,
    objectPos: "center",
    description: "KIPP Texas is a network of public charter schools creating a path to college and career for students in underserved communities.",
    details: "Virtually Verbal partnered with KIPP Texas to expand access to language learning opportunities for students. Following discussions with KIPP leadership, including the CEO, our volunteers taught introductory Chinese and French classes to students, providing engaging lessons that encouraged cultural exploration and language development. This collaboration reflects Virtually Verbal's commitment to making language education accessible while fostering global perspectives among young learners.",
    link: "https://kipptexas.org/",
    linkLabel: "Visit KIPP Public Schools",
    color: "bg-[#D6E6FA]",
  },
  {
    title: "School for Children of the World",
    location: "Cortés, Honduras",
    status: "Active",
    image: SCW_IMG,
    objectPos: "center",
    description: "SCW supports underserved communities through education and empowerment programs in Honduras.",
    details: "Virtually Verbal partnered with SCW to provide English language instruction to teachers in underserved communities. During the pilot program, volunteers taught weekly classes to five teachers, helping build their English communication skills and confidence. Building on the success of the pilot, Virtually Verbal is expanding the partnership to offer one-hour weekly English lessons to middle school students over an eight-week program. We are currently recruiting volunteer tutors, including two native Spanish-speaking tutors from Honduras.",
    link: "https://schoolsforchildren.org/",
    linkLabel: "Learn more about SCW",
    color: "bg-[#FFAEBC]",
  },
  {
    title: "i-Saksham",
    location: "Bihar, India",
    status: "Past Project",
    image: ISAKSHAM_IMG,
    objectPos: "50% 30%",
    description: "i-Saksham is a nonprofit organization dedicated to empowering girls in rural India to achieve their academic, personal, and career goals through education and leadership development.",
    details: "Virtually Verbal partnered with i-Saksham. Our volunteer tutors led Hindi-based English as a Second Language (ESL) classes for small groups of 8–10 students, helping strengthen their English communication skills and confidence. Through this partnership, we created a supportive learning environment where students could develop their speaking abilities, express themselves with confidence, and broaden their educational opportunities.",
    link: "https://www.i-saksham.org/",
    linkLabel: "Visit i-Saksham",
    color: "bg-[#FBE7C6]",
  },
];

const ProjectCard = ({ project, index }) => {
  const isEven = index % 2 === 0;

  return (
    <div className={`grid lg:grid-cols-2 gap-8 items-center ${!isEven ? "lg:direction-rtl" : ""}`} data-testid={`project-${index}`}>
      <div className={`${!isEven ? "lg:order-2" : ""}`}>
        <div className={`overflow-hidden rounded-full border-2 border-[#0A0A0A] ${project.color} aspect-square w-full max-w-xs mx-auto`} style={{ boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)" }}>
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" style={{ objectPosition: project.objectPos }} loading="lazy" />
        </div>
      </div>
      <div className={`${!isEven ? "lg:order-1" : ""}`}>
        <div className="flex items-center gap-3 mb-4">
          <span className={`${project.color} border-2 border-[#0A0A0A] rounded-full px-3 py-0.5 text-xs font-bold uppercase tracking-wider`}>
            {project.status}
          </span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold mb-1">{project.title}</h3>
        <p className="text-sm font-semibold text-[#3C8DE4] mb-4">{project.location}</p>
        <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">{project.description}</p>
        <p className="text-sm text-[#4A4A4A] leading-relaxed mb-5">{project.details}</p>
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#3C8DE4] hover:underline"
          data-testid={`project-link-${index}`}
        >
          {project.linkLabel} <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="py-20 sm:py-28 px-6 bg-gradient-to-b from-[#FBE7C6]/40 to-[#FDFBF7]">
        <div className="container mx-auto text-center max-w-3xl">
          <div className="inline-block bg-[#3C8DE4] text-white border-2 border-[#0A0A0A] rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider mb-6" data-testid="projects-badge">
            Our Projects
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            Building Bridges Through Education
          </h1>
          <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed">
            Community-driven language learning initiatives that create educational opportunities and global connections.
          </p>
        </div>
      </section>

      {/* Projects List */}
      <section className="py-16 px-6">
        <div className="container mx-auto space-y-20">
          {projects.map((project, index) => (
            <ProjectCard key={project.title} project={project} index={index} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#3C8DE4] border-y-2 border-[#0A0A0A]">
        <div className="container mx-auto text-center max-w-xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Want to Partner With Us?</h2>
          <p className="text-white/80 text-sm mb-6">
            We are always looking for new partners to expand access to language education worldwide.
          </p>
          <a
            href="mailto:officialvirtuallyverbal@gmail.com"
            className="inline-block bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-full font-bold uppercase tracking-wider px-8 py-3 text-sm hover:bg-[#FBE7C6] transition-colors"
            style={{ boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)" }}
            data-testid="partner-cta"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default ProjectsPage;
